"use client";

import { useQuery } from "@apollo/client";
import { LiquidPill } from "components/ui/v2";
import { PageEmptyStateV2, PageMetaBarV2 } from "components/ui/v2/maintainer";
import { PageShell } from "components/ui/v2/layout/page-shell";
import { gqlCertificateUser } from "gql";
import { IGetAllUserCertificate } from "gql/Certificate-user/queries";
import { AlertCircle, Award, RefreshCw } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { CertificateCardV2 } from "./CertificateCardV2";
import { CertificatesLoadingStateV2 } from "./CertificatesLoadingStateV2";

export function CertificatesUserPageV2() {
  const { t } = useTranslation("common");

  const { loading, data, error, refetch } = useQuery<IGetAllUserCertificate>(
    gqlCertificateUser.queries.MY_CERTIFICATES,
    { fetchPolicy: "network-only", notifyOnNetworkStatusChange: true }
  );

  const certificates = data?.getAllUserCertificate ?? [];
  const hasLoadedData = data != null;
  const hasCertificates = certificates.length > 0;
  const isQueryError = Boolean(error) && !hasLoadedData;
  const showListLoading = loading && !hasLoadedData;
  const isEmpty = !showListLoading && (isQueryError || !hasCertificates);

  return (
    <PageShell
      header={{
        title: t("Mis certificaciones"),
        description: t("Certificaciones que has obtenido en Groowcity"),
        leadingIcon: <Award className="h-10 w-10 shrink-0" />,
        badges: (
          <>
            <LiquidPill
              variant="violet"
              appearance="tinted"
              shadow="neutral"
              size="sm"
              icon={<Award className="h-4 w-4" />}
              text={
                <>
                  <span className="font-semibold text-current">
                    {certificates.length}
                  </span>{" "}
                  {t("Certificados")}
                </>
              }
            />
            <LiquidPill
              variant="violet"
              appearance="tinted"
              shadow="neutral"
              size="sm"
              icon={<Award className="h-4 w-4" />}
              text={
                <>
                  <span className="font-semibold text-current">
                    {certificates.length}
                  </span>{" "}
                  {t("Emitidos")}
                </>
              }
            />
          </>
        ),
        particleIcon: Award,
      }}
      loading={showListLoading}
      loadingState={
        <CertificatesLoadingStateV2
          title={t("Cargando certificados")}
          description={t("Preparando tus certificaciones")}
        />
      }
      isEmpty={isEmpty}
      emptyState={
        isQueryError ? (
          <PageEmptyStateV2
            icon={<AlertCircle className="h-8 w-8" />}
            title={t("No se pudieron cargar los certificados")}
            description={t(
              "Ocurrió un error al obtener tus certificaciones. Intenta de nuevo."
            )}
            actions={
              <LiquidPill
                as="button"
                type="button"
                variant="violet"
                appearance="tinted"
                size="md"
                icon={<RefreshCw className="h-3.5 w-3.5" />}
                text={t("Intentar de nuevo")}
                aria-label={t("Intentar de nuevo")}
                onClick={() => {
                  void refetch();
                }}
              />
            }
          />
        ) : (
          <PageEmptyStateV2
            icon={<Award className="h-8 w-8" />}
            title={t("Aun no tienes certificados")}
            description={t(
              "Aqui encontraras las certificaciones que has obtenido en Groowcity"
            )}
          />
        )
      }
      metaBar={
        hasCertificates && !showListLoading ? (
          <PageMetaBarV2
            icon={Award}
            summary={
              <>
                {t("Mostrando")} {certificates.length} {t("de")}{" "}
                {certificates.length} {t("certificaciones")} {" · "}
                <span className="font-semibold text-current">
                  {certificates.length}
                </span>{" "}
                {t("Emitidos")}
              </>
            }
          />
        ) : undefined
      }
    >
      {hasCertificates && !showListLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {certificates.map((certificate) => (
            <CertificateCardV2
              key={certificate.id}
              certificate={certificate}
              onViewCertificate={(id) =>
                window.open(`/certificate/${id}`, "_blank")
              }
              onDownload={(id) =>
                window.open(`/certificate/${id}/download`, "_blank")
              }
            />
          ))}
        </div>
      ) : null}
    </PageShell>
  );
}
